/**
 * @fileoverview AI Meal Planner V2 - Sprint 15.1 Enhancements
 * 
 * SPRINT 15.1 FIXES:
 * 1. API error handling with visible error banner (no silent failures)
 * 2. "How to Cook" accordion with cookingSteps rendering
 * 3. Chef's Notes section
 * 4. Fallback message when cooking steps are missing
 * 5. Price formatting to 2 decimal places
 * 6. "with_rice" meal context explanation
 * 
 * NO REDESIGN - Only these specific additions.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ChefHat,
  Users,
  Wallet,
  X,
  Loader2,
  Clock,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Check,
  AlertCircle,
  MapPin,
  CookingPot,
  Edit3,
} from 'lucide-react';
import api from '../../lib/api';
import { AxiosError } from 'axios';

// Types (same as V1)
interface MatchedIngredient {
  name: string;
  quantity: string;
  category: string;
  matched?: {
    productId: string;
    productName: string;
    vendorName: string;
    realPrice: number;
    unit: string;
    available: boolean;
  };
}

// Sprint 15.3: Disambiguation types
interface DishOption {
  dishId: string;
  name: string;
  emoji: string;
  description: string;
  cost: number;
  difficulty: string;
}

interface DishDisambiguation {
  query: string;
  status: 'ambiguous' | 'ready' | 'not_found';
  options?: DishOption[];
  dishId?: string;
  name?: string;
}

interface DisambiguationResponse {
  needsSelection: true;
  mealPlanId: string;
  dishes: DishDisambiguation[];
  allowAiFallback: boolean;
  message: string;
}

interface CookingStep {
  stepNumber: number;
  title: string;
  minutes: number;
  bullets: string[];
  tips?: string[];
}

interface Dish {
  name: string;
  description: string;
  servings: number;
  prepTime: string;
  difficulty: string;
  ingredients: MatchedIngredient[];
  cookingTips?: string;
  // V2 additions
  cookingSteps?: CookingStep[];
  chefNotes?: string[];
  // Sprint 15.2: Source indicator for multi-dish
  source?: 'v2' | 'v1';
}

interface CartItem {
  productId: string;
  productName: string;
  vendorId: string;
  vendorName: string;
  quantity: number;
  unitPrice: number;
}

interface MealPlanResult {
  success: boolean;
  source: string;
  mealPlanId?: string;
  dishes: Dish[];
  pricing?: {
    aiEstimate: number;
    realTotal: number;
    matchedCount: number;
    totalIngredients: number;
    savings: number;
  };
  // V2 additions
  servingsSummary?: {
    total: number;
    adults: number;
    kids: number;
    mealContext: string;
  };
  portionNotes?: string[];
  totalEstimatedCost?: number;
  cartItems?: CartItem[];
  budgetTips?: string;
  // Sprint 15.2: Multi-dish summary
  summary?: {
    totalDishes: number;
    v2Dishes: number;
    v1Dishes: number;
  };
}

const EXAMPLE_PROMPTS = ["🍗 Adobo", "🍲 Sinigang", "🍽️ Adobo, Sinigang", "🍨 Halo Halo"];

// Helper: Convert underscores to spaces and title case for display
const formatIngredientName = (name: string) => 
  name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function MealPlannerV2() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [servings, setServings] = useState(4);
  const [budget, setBudget] = useState<'budget' | 'moderate' | 'premium'>('moderate');
  const [loading, setLoading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [result, setResult] = useState<MealPlanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedDish, setExpandedDish] = useState<number | null>(0);
  const [addedToCart, setAddedToCart] = useState(false);
  
  // V2 state additions
  const [inputCollapsed, setInputCollapsed] = useState(false);
  const [cookingStepsExpanded, setCookingStepsExpanded] = useState<number | null>(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  
  // Sprint 15.3: Disambiguation state
  const [disambiguationData, setDisambiguationData] = useState<DisambiguationResponse | null>(null);
  const [selectedDishes, setSelectedDishes] = useState<Map<string, { dishId: string; name: string }>>(new Map());

  const handleSubmit = async (skipDisambiguation = false) => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setAddedToCart(false);
    setInputCollapsed(false);
    
    // Clear disambiguation if starting fresh
    if (!skipDisambiguation) {
      setDisambiguationData(null);
      setSelectedDishes(new Map());
    }

    try {
      // Build request - include selected dishes if we have them
      const requestBody: any = {
        query, 
        servings, 
        budget,
        mealContext: 'with_rice',
      };
      
      // If user made selections from disambiguation, include them
      if (selectedDishes.size > 0) {
        requestBody.selectedDishes = Array.from(selectedDishes.entries()).map(([originalQuery, selection]) => ({
          originalQuery,
          dishId: selection.dishId,
        }));
      }
      
      // Try V2 endpoint first (NOW SUPPORTS MULTI-DISH + DISAMBIGUATION!)
      const response = await api.post('/api/ai/meal-plan/v2', requestBody);
      
      // Check for valid response
      if (!response.data || response.status !== 200) {
        throw new Error('Invalid response from API');
      }
      
      // Sprint 15.3: Check if disambiguation is needed
      if (response.data.needsSelection) {
        setDisambiguationData(response.data as DisambiguationResponse);
        setLoading(false);
        return; // Stop here and show picker UI
      }
      
      // Clear disambiguation if we got a real result
      setDisambiguationData(null);
      
      // Transform V2 response to match expected shape
      const v2Data = response.data;
      
      // Handle both single-dish (old format) and multi-dish (new format) responses
      const dishesArray = v2Data.dishes || (v2Data.dish ? [{
        source: 'v2',
        dish: v2Data.dish,
        normalizedIngredients: v2Data.normalizedIngredients,
        cookingSteps: v2Data.cookingSteps,
        chefNotes: v2Data.chefNotes,
        aiEstimateRange: v2Data.aiEstimateRange,
        servingsSummary: v2Data.servingsSummary,
        portionNotes: v2Data.portionNotes,
      }] : []);
      
      // Transform to unified format
      const transformedDishes = dishesArray.map((dishData: any) => {
        if (dishData.source === 'v2') {
          // V2 format (structured)
          return {
            name: dishData.dish?.name || 'Unknown Dish',
            description: dishData.dish?.description || '',
            servings: dishData.servingsSummary?.total || servings,
            prepTime: `${dishData.cookingSteps?.totalTimeMinutes || 45} mins`,
            difficulty: dishData.dish?.difficulty || 'medium',
            ingredients: dishData.normalizedIngredients?.map((ing: any) => ({
              name: formatIngredientName(ing.canonicalName),
              quantity: `${ing.quantity} ${ing.unit.replace(/_/g, ' ')}`,
              category: ing.category,
            })) || [],
            cookingSteps: dishData.cookingSteps?.steps,
            chefNotes: dishData.chefNotes || [],
            source: 'v2',
          };
        } else if (dishData.source === 'v1') {
          // V1 format (OpenAI - simpler structure)
          return {
            name: dishData.dish?.name || 'Unknown Dish',
            description: dishData.dish?.description || '',
            servings,
            prepTime: '45 mins',
            difficulty: dishData.dish?.difficulty || 'medium',
            // V1 ingredients are also in normalizedIngredients from the API
            ingredients: dishData.normalizedIngredients?.map((ing: any) => ({
              name: formatIngredientName(ing.canonicalName || ing.name || 'Unknown'),
              quantity: `${ing.quantity || ''} ${(ing.unit || '').replace(/_/g, ' ')}`.trim() || ing.quantity,
              category: ing.category || 'other',
            })) || [],
            cookingTips: dishData.cookingTips || '',
            cookingSteps: null, // V1 doesn't have structured steps
            chefNotes: [],
            source: 'v1',
          };
        }
        return null;
      }).filter(Boolean);
      
      // Calculate total pricing across all dishes
      const totalAiEstimate = dishesArray.reduce((sum: number, dish: any) => {
        return sum + (dish.aiEstimateRange?.mid || 0);
      }, 0);
      
      setResult({
        success: true,
        source: v2Data.summary ? 'hybrid' : 'openai_v2',
        mealPlanId: v2Data.mealPlanId,
        dishes: transformedDishes,
        pricing: {
          aiEstimate: totalAiEstimate,
          realTotal: 0, // Will be filled by routing
          matchedCount: 0,
          totalIngredients: dishesArray.reduce((sum: number, d: any) => 
            sum + (d.normalizedIngredients?.length || d.ingredients?.length || 0), 0),
          savings: 0,
        },
        servingsSummary: dishesArray[0]?.servingsSummary,
        portionNotes: dishesArray[0]?.portionNotes,
        summary: v2Data.summary, // Multi-dish summary
      });
      
      // Collapse input section after successful result
      setInputCollapsed(true);
      
    } catch (err) {
      // Sprint 15.1: Visible error handling - no silent failures
      const axiosError = err as AxiosError<{ error?: string; fallback?: string }>;
      const statusCode = axiosError.response?.status;
      const errorMessage = axiosError.response?.data?.error;
      
      // Log for debugging
      console.error('[MealPlannerV2] API error:', { statusCode, errorMessage, url: '/api/ai/meal-plan/v2' });
      
      // Handle specific error cases
      const shouldFallbackToV1 = 
        statusCode === 404 && (errorMessage?.includes('No matching dish') || errorMessage?.includes('dish found')) ||
        statusCode === 503;
      
      if (statusCode === 404 && !shouldFallbackToV1) {
        // True 404 - endpoint not registered
        setError('Meal Planner V2 endpoint not found. Please ensure the API is deployed with Sprint 15 routes.');
        return;
      } else if (statusCode === 400) {
        setError(errorMessage || 'Invalid request. Please check your input.');
        return;
      }
      
      // Fallback to V1 endpoint for unknown dishes or when V2 is disabled
      if (shouldFallbackToV1) {
        console.log('[MealPlannerV2] Dish not in V2 database, falling back to V1...');
      }
      
      try {
        console.log('[MealPlannerV2] Trying V1 fallback: /api/ai/meal-plan/full');
        const fallback = await api.post('/api/ai/meal-plan/full', { query, servings, budget });
        
        if (!fallback.data) {
          throw new Error('V1 fallback returned empty response');
        }
        
        setResult(fallback.data);
        setInputCollapsed(true);
        setError(null);
      } catch (fallbackErr) {
        const fbError = fallbackErr as AxiosError<{ error?: string }>;
        console.error('[MealPlannerV2] V1 fallback also failed:', fbError.response?.status);
        setError(
          fbError.response?.data?.error || 
          'Failed to generate meal plan. Please check your connection and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFindStores = async () => {
    if (!result?.dishes?.[0]?.ingredients) return;
    
    setRoutingLoading(true);
    try {
      // Call routing endpoint (stub for now - would need user location)
      // For now, just show loading then fall back to simple add-to-cart
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to a routing result or just enable cart
      setRoutingLoading(false);
      // In full implementation, this would show vendor options
      
    } catch {
      setRoutingLoading(false);
      setError('Unable to find stores. Try adding directly to cart.');
    }
  };

  const handleAddToCart = async () => {
    if (!result?.cartItems?.length) return;
    setAddingToCart(true);
    try {
      await api.post('/api/ai/meal-plan/add-to-cart', {
        items: result.cartItems.map(item => ({
          productId: item.productId,
          vendorId: item.vendorId,
          quantity: item.quantity || 1
        }))
      });
      setAddedToCart(true);
    } catch {
      setError('Failed to add items to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setAddedToCart(false);
    setQuery('');
    setError(null);
    setInputCollapsed(false);
    setCookingStepsExpanded(null);
    setDisambiguationData(null);
    setSelectedDishes(new Map());
  };
  
  // Sprint 15.3: Handle disambiguation selection
  const handleDishSelection = (queryStr: string, dishId: string, dishName: string) => {
    setSelectedDishes(prev => {
      const newMap = new Map(prev);
      newMap.set(queryStr, { dishId, name: dishName });
      return newMap;
    });
  };
  
  // Sprint 15.3: Check if all ambiguous dishes have been selected
  const allSelectionsComplete = () => {
    if (!disambiguationData) return false;
    const ambiguousDishes = disambiguationData.dishes.filter(d => d.status === 'ambiguous');
    return ambiguousDishes.every(d => selectedDishes.has(d.query));
  };
  
  // Sprint 15.3: Submit with selections
  const handleSubmitWithSelections = () => {
    handleSubmit(true);
  };

  const toggleInputCollapse = () => {
    setInputCollapsed(!inputCollapsed);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="min-h-full flex items-start justify-center pt-8 pb-24 px-3">
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
          
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-purple-100 rounded-lg">
                <Sparkles className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 text-sm">AI Meal Planner</h2>
                <p className="text-xs text-gray-500">Powered by OpenAI</p>
              </div>
            </div>
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-4">
            
            {/* === SPRINT 15: COLLAPSIBLE INPUT SECTION === */}
            <AnimatePresence mode="wait">
              {result && inputCollapsed ? (
                // Collapsed header showing summary
                <motion.div
                  key="collapsed-input"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4"
                >
                  <button 
                    onClick={toggleInputCollapse}
                    className="w-full p-3 bg-purple-50 rounded-xl flex items-center justify-between hover:bg-purple-100 transition"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <ChefHat className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-semibold text-purple-800 text-sm">
                          {result.dishes[0]?.name || query}
                        </p>
                        <p className="text-xs text-purple-600">
                          {servings} servings • {budget}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-purple-600">Change</span>
                      <Edit3 className="w-4 h-4 text-purple-600" />
                    </div>
                  </button>
                </motion.div>
              ) : (
                // Full input section
                <motion.div
                  key="expanded-input"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <ChefHat className="w-4 h-4 inline mr-1" />
                      What do you want to cook?
                    </label>
                    <textarea
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="e.g., Adobo for dinner..."
                      className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 resize-none text-base"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        <Users className="w-3 h-3 inline" /> Servings
                      </label>
                      <select value={servings} onChange={(e) => setServings(Number(e.target.value))}
                        className="w-full p-2 border rounded-lg text-sm">
                        {[1,2,3,4,5,6,8,10].map(n => <option key={n} value={n}>{n} people</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        <Wallet className="w-3 h-3 inline" /> Budget
                      </label>
                      <select value={budget} onChange={(e) => setBudget(e.target.value as any)}
                        className="w-full p-2 border rounded-lg text-sm">
                        <option value="budget">💵 Budget</option>
                        <option value="moderate">💰 Moderate</option>
                        <option value="premium">💎 Premium</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {EXAMPLE_PROMPTS.map(p => (
                      <button key={p} onClick={() => setQuery(p.replace(/^[^\w]+/, '').trim())}
                        className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100">
                        {p}
                      </button>
                    ))}
                  </div>

                  {/* Generate Button - show when no result OR when input is expanded */}
                  <button onClick={() => handleSubmit()} disabled={!query.trim() || loading}
                    className="w-full mt-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> AI is cooking...</> 
                             : <><Sparkles className="w-5 h-5" /> Generate Meal Plan</>}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 rounded-xl text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            {/* Sprint 15.3: DISAMBIGUATION UI */}
            {disambiguationData && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 space-y-4"
              >
                <div className="p-3 bg-purple-50 rounded-xl">
                  <h3 className="font-semibold text-purple-800 text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Let's customize your meal plan
                  </h3>
                  <p className="text-xs text-purple-600 mt-1">
                    Pick your preferred variant for each dish
                  </p>
                </div>
                
                {disambiguationData.dishes.map((dish, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-3">
                    {dish.status === 'ambiguous' && dish.options ? (
                      // Ambiguous dish - show picker
                      <>
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {dish.query.charAt(0).toUpperCase() + dish.query.slice(1)}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {dish.options.map((option) => {
                            const isSelected = selectedDishes.get(dish.query)?.dishId === option.dishId;
                            return (
                              <button
                                key={option.dishId}
                                onClick={() => handleDishSelection(dish.query, option.dishId, option.name)}
                                className={`p-3 rounded-lg border-2 text-left transition ${
                                  isSelected
                                    ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 bg-white hover:border-purple-300'
                                }`}
                              >
                                <div className="text-xl mb-1">{option.emoji}</div>
                                <p className="font-medium text-sm text-gray-800">{option.name}</p>
                                <p className="text-xs text-gray-500 mt-0.5">₱{option.cost} • {option.difficulty}</p>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : dish.status === 'ready' ? (
                      // Ready dish - show as confirmed
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{dish.name}</p>
                          <p className="text-xs text-green-600">Ready to cook!</p>
                        </div>
                      </div>
                    ) : (
                      // Not found - will use AI
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{dish.query}</p>
                          <p className="text-xs text-blue-600">AI will generate this for you</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* "Let AI decide" option */}
                {disambiguationData.allowAiFallback && (
                  <button 
                    onClick={() => {
                      // Mark all ambiguous dishes for AI fallback
                      const newSelections = new Map<string, { dishId: string; name: string }>();
                      disambiguationData.dishes.forEach(d => {
                        if (d.status === 'ambiguous') {
                          newSelections.set(d.query, { dishId: 'AI_FALLBACK', name: 'AI will decide' });
                        }
                      });
                      setSelectedDishes(newSelections);
                      setTimeout(() => handleSubmit(true), 100);
                    }}
                    className="w-full p-3 bg-blue-50 border-2 border-dashed border-blue-200 rounded-xl text-left hover:bg-blue-100 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-800">🤖 Let AI decide for me</p>
                        <p className="text-xs text-blue-600">Skip selection - AI will pick the best match</p>
                      </div>
                    </div>
                  </button>
                )}
                
                {/* Generate button */}
                <button
                  onClick={handleSubmitWithSelections}
                  disabled={!allSelectionsComplete() || loading}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</>
                  ) : (
                    <><ChefHat className="w-5 h-5" /> Generate My Meal Plan</>
                  )}
                </button>
                
                {/* Back button */}
                <button
                  onClick={resetForm}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-700"
                >
                  ← Start over
                </button>
              </motion.div>
            )}

            {/* Results - only show when input is collapsed and not in disambiguation mode */}
            {result && result.dishes && result.dishes.length > 0 && inputCollapsed && !disambiguationData && (
              <div className="space-y-3">
                
                {/* Multi-dish Summary (Sprint 15.2) */}
                {result.dishes.length > 1 && (
                  <div className="p-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl">
                    <p className="text-sm font-semibold text-purple-800">
                      🍽️ {result.dishes.length} dishes in your meal plan
                    </p>
                    {result.summary && result.summary.v1Dishes > 0 && (
                      <p className="text-xs text-purple-600 mt-1">
                        {result.summary.v2Dishes} from our recipes • {result.summary.v1Dishes} AI-generated
                      </p>
                    )}
                  </div>
                )}

                {/* Pricing Cards */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-3 bg-purple-50 rounded-xl text-center">
                    <p className="text-xs text-purple-600 mb-1">🤖 AI Estimate</p>
                    <p className="text-lg font-bold text-purple-700">
                      ₱{(result.pricing?.aiEstimate || result.totalEstimatedCost)?.toFixed(2) || '—'}
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-xl text-center">
                    <p className="text-xs text-green-600 mb-1">🏪 Real Price</p>
                    <p className="text-lg font-bold text-green-700">
                      {result.pricing?.realTotal ? `₱${result.pricing.realTotal.toFixed(2)}` : 'Tap below'}
                    </p>
                  </div>
                </div>

                {result.pricing && result.pricing.matchedCount > 0 && (
                  <div className="p-2 bg-blue-50 rounded-lg text-center text-xs text-blue-700">
                    ✅ {result.pricing.matchedCount}/{result.pricing.totalIngredients} ingredients found
                    {result.pricing.savings > 0 && (
                      <span className="text-green-600 font-medium"> • Save ₱{result.pricing.savings.toFixed(2)}!</span>
                    )}
                  </div>
                )}

                {/* Sprint 15.1: Simple "with rice" explanation (user-friendly) */}
                {result.servingsSummary?.mealContext === 'with_rice' && (
                  <div className="p-2 bg-green-50 rounded-lg text-xs text-green-700">
                    🍚 <strong>Served with rice</strong> — Portion sizes are adjusted since rice is included as the main carb.
                  </div>
                )}

                {/* Dishes */}
                {result.dishes.map((dish, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpandedDish(expandedDish === index ? null : index)}
                      className="w-full p-3 text-left flex items-center justify-between"
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-800 text-sm truncate">{dish.name}</h4>
                        <p className="text-xs text-gray-500 truncate">{dish.description}</p>
                        <div className="flex gap-2 mt-1 text-xs text-gray-400">
                          <span><Clock className="w-3 h-3 inline" /> {dish.prepTime}</span>
                          <span className="capitalize">• {dish.difficulty}</span>
                        </div>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 flex-shrink-0 transition ${expandedDish === index ? 'rotate-180' : ''}`} />
                    </button>

                    {expandedDish === index && dish.ingredients && (
                      <div className="px-3 pb-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-600 mt-2 mb-2">Ingredients:</p>
                        <div className="space-y-1">
                          {dish.ingredients.map((ing, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-gray-600">
                                {ing.matched?.available ? '✅' : '⚪'} {ing.quantity} {ing.name}
                              </span>
                              {ing.matched?.available && (
                                <span className="text-green-600 font-medium">₱{ing.matched.realPrice}</span>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* === SPRINT 15.1: COOKING STEPS ACCORDION === */}
                        <div className="mt-3">
                          {dish.cookingSteps && dish.cookingSteps.length > 0 ? (
                            <>
                              <button
                                onClick={() => setCookingStepsExpanded(
                                  cookingStepsExpanded === index ? null : index
                                )}
                                className="w-full flex items-center justify-between p-2 bg-orange-50 rounded-lg text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <CookingPot className="w-4 h-4 text-orange-600" />
                                  <span className="text-sm font-medium text-orange-800">
                                    How to Cook ({dish.cookingSteps.length} steps)
                                  </span>
                                </div>
                                {cookingStepsExpanded === index ? (
                                  <ChevronUp className="w-4 h-4 text-orange-600" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-orange-600" />
                                )}
                              </button>
                              
                              <AnimatePresence>
                                {cookingStepsExpanded === index && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-2 space-y-2"
                                  >
                                    {dish.cookingSteps.map((step, stepIdx) => (
                                      <div key={stepIdx} className="p-2 bg-white rounded-lg border border-orange-100">
                                        <div className="flex items-start gap-2">
                                          <span className="flex-shrink-0 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                                            {step.stepNumber}
                                          </span>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm text-gray-800">{step.title}</p>
                                            <p className="text-xs text-gray-500">{step.minutes} mins</p>
                                            <ul className="mt-1 space-y-0.5">
                                              {step.bullets.map((bullet, bIdx) => (
                                                <li key={bIdx} className="text-xs text-gray-600 flex items-start gap-1">
                                                  <span className="text-gray-400">•</span>
                                                  {bullet}
                                                </li>
                                              ))}
                                            </ul>
                                            {step.tips && step.tips.length > 0 && (
                                              <p className="mt-1 text-xs text-amber-700 italic">
                                                💡 {step.tips[0]}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    
                                    {/* Sprint 15.1: Chef's Notes */}
                                    {dish.chefNotes && dish.chefNotes.length > 0 && (
                                      <div className="p-2 bg-amber-50 rounded-lg">
                                        <p className="text-xs font-medium text-amber-800 mb-1">👨‍🍳 Chef's Notes:</p>
                                        <ul className="space-y-0.5">
                                          {dish.chefNotes.slice(0, 3).map((note, nIdx) => (
                                            <li key={nIdx} className="text-xs text-amber-700">• {note}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </>
                          ) : dish.cookingTips ? (
                            /* V1 cooking tips fallback */
                            <div className="p-2 bg-amber-50 rounded-lg text-xs text-amber-800">
                              💡 {dish.cookingTips}
                            </div>
                          ) : (
                            /* Sprint 15.1: Fallback when cooking steps unavailable */
                            <div className="p-2 bg-gray-50 rounded-lg text-xs text-gray-500 flex items-center gap-2">
                              <CookingPot className="w-3 h-3 flex-shrink-0" />
                              <span>Cooking steps unavailable at the moment. Try again later!</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {result.budgetTips && (
                  <div className="p-3 bg-amber-50 rounded-xl text-xs text-amber-800">
                    💰 <strong>Tip:</strong> {result.budgetTips}
                  </div>
                )}

                {/* === SPRINT 15: FIND STORES CTA === */}
                <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                  <h4 className="font-semibold text-blue-800 text-sm mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Get Ingredients
                  </h4>
                  <p className="text-xs text-blue-600 mb-3">
                    Find the best stores near you with real prices
                  </p>
                  <button 
                    onClick={handleFindStores}
                    disabled={routingLoading}
                    className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {routingLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Finding stores...</>
                    ) : (
                      <><MapPin className="w-4 h-4" /> Find Stores & Prices</>
                    )}
                  </button>
                </div>

                {/* Add to Cart Button (shown after routing or as fallback) */}
                {!addedToCart && result.cartItems && result.cartItems.length > 0 && (
                  <button onClick={handleAddToCart} disabled={addingToCart}
                    className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                    {addingToCart ? <><Loader2 className="w-5 h-5 animate-spin" /> Adding...</>
                                 : <><ShoppingCart className="w-5 h-5" /> Add {result.cartItems.length} Items to Cart</>}
                  </button>
                )}

                {/* Success Message */}
                {addedToCart && (
                  <div className="p-4 bg-green-50 rounded-xl text-center">
                    <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-medium text-green-800">Added to Cart!</p>
                    <button onClick={() => navigate('/cart')} 
                      className="mt-2 text-sm text-green-600 underline">
                      Go to Cart →
                    </button>
                  </div>
                )}

                {/* Plan Another Meal */}
                <button onClick={resetForm}
                  className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition">
                  🍳 Plan Another Meal
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
